import { createAction } from 'redux-actions';
import { timeDay } from 'd3-time';

import { idCreator, metaCreator, payloadCreator } from './creator';
import { checkForCachedDataAction, requestAction, secondTimeFormat } from './request';
import fetch, {
  Api,
  getJson,
  handleError,
  noCacheRequestConfig,
  setFhirAuthToken,
} from './fetch';
import { getDataSourceStates } from './dataSources';
import { setViewDates } from './time';

export const authAction = createAction('AUTHENTICATE', payloadCreator, metaCreator);

export const loginAction = createAction('LOGIN', payloadCreator, metaCreator);

export const logoutAction = createAction('LOGOUT', payloadCreator, metaCreator);

export const userAction = createAction('USER_LOADED', payloadCreator, metaCreator);


export const isMocked = user => (user.id && (user.id.indexOf('demo-user') === 0));

let userFetchPromise;

const reject = (response) => {
  const error = new Error(`${response.statusText || 'Authentication error!'} ${response.status}`);
  error.response = response;
  throw error;
};

const checkAccess = (response) => {
  if (response.status === 403 || response.status === 404) {
    // allow these to pass if 'access' is present...
    return getJson(response)
      .then((json) => {
        if (json.access) {
          return {
            json: () => json,
          };
        }
        return reject(response);
      });
  }
  if (response.status < 200
    || response.status === 204
    || response.status === 205
    || response.status >= 300) {
    return reject(response);
  }
  return response;
};

// TODO: Should check whether we need to log out from Facebook / Google?
export const logout = () => (dispatch) => {
  const id = idCreator();
  const description = `Logging out at ${secondTimeFormat(new Date())}`;
  userFetchPromise = undefined;
  setFhirAuthToken(null);
  return checkForCachedDataAction(requestAction({ id, description }), dispatch)
    .then(() => fetch(`${Api}/logout`, noCacheRequestConfig))
    .then(handleError)
    .then(response => dispatch(logoutAction({ id, description, data: response })))
    .catch(error => dispatch(logoutAction({ id, description, error })))
    // eslint-disable-next-line no-console
    .catch(console.error);
};

export const login = ({
  description,
  error,
  id,
  user,
  service,
}) => (dispatch) => {
  if (error) {
    dispatch(loginAction({ id, description, error }));
    return error;
  }
  const { services } = user || {};
  if (services && !isMocked(user)) {
    dispatch(getDataSourceStates());
  }
  dispatch(loginAction({ id, description, data: { user, service } }));
  return user;
};

export const checkLoginStatus = refresh => ((dispatch) => {
  const id = idCreator();
  const description = `Checking login status at ${secondTimeFormat(new Date())}`;
  return checkForCachedDataAction(requestAction({ id, description }), dispatch)
    .then(() => {
      if (navigator.userAgent === 'ReactSnap') {
        throw new Error('No user data for snapshots');
      }
      if (refresh || !userFetchPromise) {
        const { search } = document.location;
        const s = `${search ? `${search}&` : '?'}n=${new Date().getTime()}`;
        userFetchPromise = fetch(`${Api}/user${s}`, noCacheRequestConfig)
          .then(checkAccess)
          .then(getJson);
      }
      return userFetchPromise
        .then((user) => {
          let verifyPromise;
          switch (user.login) {
            /*
            case 'google':
              loginService = 'google';
              verifyPromise = dispatch(loginWithGoogle(true))
                .then(action => action.payload);
              break;
            case 'facebook':
              loginService = 'facebook';
              verifyPromise = dispatch(autoLoginWithFacebook())
                .then(action => action.payload);
              break;
              */
            default:
              verifyPromise = Promise.resolve({ user });
          }

          // TODO: let's not verify the data of each connected data source.
          // It just takes time!
          return verifyPromise
            .then((data) => {
              const { user } = data;
              const { access } = user || {};
              const { accessToken } = user || {};
              const { state } = access || {};
              if (state) {
                setFhirAuthToken(state);
              } else if (accessToken) {
                setFhirAuthToken(accessToken);
              } else {
                setFhirAuthToken(null);
              }

              const { services } = user || {};
              if (services && !isMocked(user)) {
                dispatch(getDataSourceStates())
                  .finally((result = {}) => {
                    const today = timeDay(new Date());
                    dispatch(setViewDates({
                      modifier: 'selectByDay',
                      selectedDates: [timeDay.offset(today, -14).toISOString(), today.toISOString()],
                      selectBy: 'days',
                      user: { services: result.payload || [] },
                    }));
                  });
              }
              return dispatch(authAction({ id, description, data }));
            });
        });
    })
    .catch((error) => {
      // OK, authentication errors come here. Just not authenticated.
      return dispatch(authAction({ id, description, data: {} }));
    });
});
